/**
 * Handicap-stroke indicator on a score mark — one muted pip per stroke the
 * player receives on the hole, stacked vertically with ~40% overlap so the
 * count reads as the height of a short column. Each pip carries a 1px paper
 * ring so overlapping pips stay distinct AND read on dark/gold filled marks.
 * Capped at 3 (real course handicaps never give a 4th stroke on one hole).
 * The bottom pip sits at (`top`, `right`); extra pips grow upward, away from
 * the sacred centred number.
 *
 * Colour is neutral `ink-500`, not an accent: a mid/high handicap draws a
 * stroke on nearly every hole, so this is near-ubiquitous secondary reference
 * ("where do I get shots") that must sit below the gross number in the
 * hierarchy — a red accent on 18 of 18 holes would flatten it.
 */
const CAP = 3
const PIP = 5 // px diameter
const PITCH = 3 // px vertical step between pips (overlap)

const StrokeDots = ({
  count,
  top,
  right,
}: {
  count: number
  /** Top offset of the bottom (first) pip, relative to the positioned parent. */
  top: number
  /** Right offset of the pips, relative to the positioned parent. */
  right: number
}) => {
  const n = Math.min(count, CAP)
  if (n <= 0) return null
  return (
    <span role="img" aria-label={`${n} slag fra hcp`}>
      {Array.from({ length: n }).map((_, i) => (
        <span
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          aria-hidden
          className="absolute rounded-full bg-ink-500"
          style={{
            width: PIP,
            height: PIP,
            top: top - i * PITCH,
            right,
            boxShadow: '0 0 0 1px var(--paper)',
          }}
        />
      ))}
    </span>
  )
}

export default StrokeDots
